import { FC } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import {
  CatButton,
  CatDrawer,
  CatHeading,
  CatList,
  CatListItem
} from 'components/blocks-components';
import { getSeoUrl } from 'utils/links';
import quickViewStyles from './quick-view-drawer.module.scss';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import styles from 'components/product-quick-view-drawer/product-quick-view-drawer.module.scss';

export interface QuickViewDrawerProps {
  data: Record<string, unknown>;
  drawerActive: boolean;
  setDrawerActive: (drawerActive: boolean) => void;
  isHeadlessPDPEnabled?: boolean;
  cartDetails;
  locationId?: string;
  itemListNameId?: string
}

const QuickViewDrawer: FC<QuickViewDrawerProps> = ({
  data,
  drawerActive,
  setDrawerActive,
  isHeadlessPDPEnabled,
  locationId,
  cartDetails,
  itemListNameId
}) => {
  const t = useTranslations();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { productCount = 0, products = [] } = data;
  const product = (productCount !== 0 && products[0]) || [];
  const productLink = isHeadlessPDPEnabled
    ? product?.partNumber
    : `${esiteUrl}/${product?.partNumber}`;
  const handleCloseDrawer = () => {
    setDrawerActive(false);
  };

  const handleSelectItemGa = () => {
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem(cartDetails?.orderHeader, product),
      partName: product && `${product?.partNumber}: ${product?.name}`,
      locationId: locationId,
      itemSummary: product,
      isSearchPageEvent: true,
      itemListId: itemListNameId,
      itemListName: itemListNameId,
      serialNumber: '',
      fitment: ''
    });
  }

  return (
    <CatDrawer
      isActive={drawerActive}
      data-testid="quick-view-drawer"
      onBlDrawerClose={handleCloseDrawer}
    >
      <div slot="header" data-testid="quick-view-drawer-header">
        {productCount !== 0 ? (
          <CatHeading slot="header" variant="title-sm">
            {`${product?.partNumber}: ${product?.name}`}
          </CatHeading>
        ) : (
          <p>{t('FYD_NO_RESULTS')}</p>
        )}
      </div>
      {productCount !== 0 && (
        <>
          <div className="d-flex">
            <div
              className={`d-flex justify-content-center align-items-center me-4 ${quickViewStyles['quick-view-drawer-thumbnail-container']}`}
            >
              <Image
                src={product?.imageURL}
                alt={product?.partNumber}
                loading="lazy"
                width={112}
                height={112}
                unoptimized
              />
            </div>
            <div className="d-flex flex-column row-gap-2 justify-content-between">
              <p className="cat-u-theme-typography-body-md mb-0">
                {product?.shortDescription}
              </p>
              <p className="cat-u-theme-typography-body-sm mb-0">
                {t('CPG_BRAND')}: {product?.manufacturer}
              </p>
              <CatButton variant="ghost" href={productLink} onClick={handleSelectItemGa}>
                {t('KITS_VIEW_PART')}
              </CatButton>
            </div>
          </div>
          <div>
            {product?.attributes?.length === 0 ? (
              <p className="cat-u-theme-typography-body-sm">
                {t('KITS_NO_SPECS_AVAILABLE')}
              </p>
            ) : (
              <CatList spacing="condensed" className={styles['cat-c-list']}>
                {product?.attributes?.map((a: Record<string, string>) => (
                  <CatListItem key={a.name} isStatic>
                    <p className="cat-u-margin-bottom-none cat-u-theme-typography-body-sm">
                      <strong>{`${a.name}: `}</strong>
                      {a.value}
                    </p>
                  </CatListItem>
                ))}
              </CatList>
            )}
          </div>
          <div slot="footer" data-testid="quick-view-drawer-footer">
            <CatButton
              variant="ghost"
              href={productLink}
              data-testid="quick-view-drawer-view-part-btn" onClick={handleSelectItemGa}
            >
              {t('KITS_VIEW_PART')}
            </CatButton>
          </div>
        </>
      )}
    </CatDrawer>
  );
};

export default QuickViewDrawer;
